import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../api/client';
import { b2bApi, b2bOpenUrl, type B2bOutput } from '../api/b2b';
import { confirmDialog } from '../shell/dialogs';

const KIND_LABEL: Record<string, string> = { offer: 'Nabídka', report: 'Report', card: 'Vizitka', guide: 'Návod' };

function fmtDate(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

function norm(s: string | null | undefined): string {
  return (s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').replace(/\b(s r o|a s|sro|as|spol|ltd|gmbh)\b/g, '').trim();
}

function matches(company: string | null, output: B2bOutput): boolean {
  const a = norm(company);
  const b = norm(output.clientName);
  if (!a || !b) return false;
  return a.includes(b) || b.includes(a) || a.split(' ')[0] === b.split(' ')[0];
}

export function CrmLeadB2b({
  leadId,
  company,
  canEdit,
  onLinked,
}: {
  leadId: number;
  company: string | null;
  canEdit: boolean;
  onLinked: (outputs: B2bOutput[]) => void;
}) {
  const [linked, setLinked] = useState<B2bOutput[] | null>(null);
  const [all, setAll] = useState<B2bOutput[] | null>(null);
  const [picking, setPicking] = useState(false);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<number | null>(null);

  const reload = useCallback(() => {
    b2bApi.list({ leadId })
      .then((rows) => { const live = rows.filter((o) => o.status !== 'archived'); setLinked(live); onLinked(live); })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Výstupy B2B se nepodařilo načíst'));
  }, [leadId, onLinked]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (!picking || all) return;
    b2bApi.list({})
      .then((rows) => setAll(rows.filter((o) => (o.kind === 'offer' || o.kind === 'report') && o.status !== 'archived')))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Výstupy B2B se nepodařilo načíst'));
  }, [picking, all]);

  const candidates = useMemo(() => {
    if (!all) return [];
    const linkedIds = new Set((linked ?? []).map((o) => o.id));
    const free = all.filter((o) => !linkedIds.has(o.id) && (!o.leadId || o.leadId === leadId));
    return [...free].sort((x, y) => Number(matches(company, y)) - Number(matches(company, x)) || (y.publishedAt ?? '').localeCompare(x.publishedAt ?? ''));
  }, [all, linked, company, leadId]);
  const suggested = candidates.filter((o) => matches(company, o));

  function link(output: B2bOutput) {
    setBusy(true);
    setError(null);
    b2bApi.update(output.id, { leadId })
      .then(() => { setPicking(false); setChoice(''); reload(); })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Propojení se nepodařilo'))
      .finally(() => setBusy(false));
  }

  async function unlink(output: B2bOutput) {
    if (!(await confirmDialog({ title: 'Odpojit výstup od leadu?', message: `${output.title} zůstane ve Výstupech B2B, jen přestane být u tohoto leadu.`, confirmLabel: 'Odpojit' }))) return;
    setBusy(true);
    setError(null);
    b2bApi.update(output.id, { leadId: null })
      .then(() => reload())
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Odpojení se nepodařilo'))
      .finally(() => setBusy(false));
  }

  function copy(output: B2bOutput) {
    navigator.clipboard?.writeText(b2bOpenUrl(output)).then(() => { setCopied(output.id); setTimeout(() => setCopied(null), 1600); }).catch(() => undefined);
  }

  return (
    <div className="udaje-section crm-b2b">
      <div className="udaje-section-head">
        <span>Výstup B2B{linked?.length ? <span className="crm-channels-count">{linked.length}</span> : null}</span>
        {canEdit && !picking && (
          <button type="button" className="crm-channels-plus" title="Propojit výstup B2B" aria-label="Propojit výstup B2B" onClick={() => setPicking(true)}>+</button>
        )}
      </div>
      <div className="udaje-card">
        {linked === null && !error && <div className="crm-channels-empty">Načítám…</div>}
        {linked?.length === 0 && !picking && (
          <div className="crm-channels-empty">
            Lead zatím nemá propojený žádný výstup B2B.
            {canEdit && suggested.length > 0 && (
              <span className="crm-b2b-suggest"> Nabízí se: <button type="button" className="crm-b2b-suggest-btn" disabled={busy} onClick={() => link(suggested[0]!)}>{suggested[0]!.title} · {suggested[0]!.clientName}</button></span>
            )}
          </div>
        )}
        {(linked ?? []).map((o) => (
          <div key={o.id} className="crm-channel crm-b2b-row">
            <span className="crm-channel-icon" aria-hidden="true"><i>{KIND_LABEL[o.kind]?.charAt(0) ?? 'B'}</i></span>
            <span className="crm-channel-body">
              <a className="crm-channel-title" href={b2bOpenUrl(o)} target="_blank" rel="noreferrer">{o.title}</a>
              <span className="crm-channel-url">{KIND_LABEL[o.kind] ?? o.kind}{o.clientName ? ` · ${o.clientName}` : ''}{o.publishedAt ? ` · publikováno ${fmtDate(o.publishedAt)}` : ''}{o.viewCount ? ` · ${o.viewCount}× zobrazeno` : ''}</span>
              <a className="crm-channel-url" href={b2bOpenUrl(o)} target="_blank" rel="noreferrer">Otevřít výstup</a>
            </span>
            {canEdit && (
              <span className="crm-channel-actions">
                <button type="button" title="Zkopírovat odkaz pro klienta" aria-label="Zkopírovat odkaz" disabled={busy} onClick={() => copy(o)}>{copied === o.id ? '✓' : '⧉'}</button>
                <button type="button" title="Odpojit od leadu" aria-label="Odpojit od leadu" disabled={busy} onClick={() => void unlink(o)}>×</button>
              </span>
            )}
          </div>
        ))}
        {canEdit && picking && (
          <form className="crm-channel-add" onSubmit={(e) => { e.preventDefault(); const o = candidates.find((c) => String(c.id) === choice); if (o) link(o); }}>
            <select className="udaje-input udaje-select" value={choice} disabled={busy || !all} onChange={(e) => setChoice(e.target.value)}>
              <option value="">{all ? 'Vyberte nabídku nebo report…' : 'Načítám výstupy…'}</option>
              {candidates.map((o) => (
                <option key={o.id} value={String(o.id)}>{matches(company, o) ? '★ ' : ''}{KIND_LABEL[o.kind] ?? o.kind} · {o.title}{o.clientName ? ` · ${o.clientName}` : ''}{o.publishedAt ? ` (${fmtDate(o.publishedAt)})` : ''}</option>
              ))}
            </select>
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !choice}>{busy ? 'Propojuji…' : 'Propojit'}</button>
            <button type="button" className="btn btn-link btn-sm crm-channel-cancel" disabled={busy} onClick={() => { setPicking(false); setChoice(''); setError(null); }}>Zrušit</button>
          </form>
        )}
        {error && <div className="crm-channels-error">{error}</div>}
      </div>
    </div>
  );
}
